// Libs
import React, { useMemo } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

// Components
import Search from '../../Search';
import Feather from '../../Feather';
import IconButton from '../../Button/IconButton/IconButton';

// Hooks
import useUI from '../../../hooks/useUI';
import useUser from '../../../hooks/useUser';
import useSearch from '../../../hooks/useSearch';
import useFeature from '../../../hooks/useFeature';

// Constants
import { kitsTabs } from '../../../constants/kits';
import { FEATURES } from '../../../constants/features';
import { SHARED_KITS_PATH } from '../../../constants/sharedKits';

// Styles
import { useStyles } from './KitsBarStyles';

const KitsBar = ({ withDeleteAll = false, onDeleteAll = () => {} }) => {
  const theme = useTheme();
  const { isKiteUser } = useUser();
  const { isTabletMode, isMobileMode } = useUI();
  const { onChange, onClearSearch } = useSearch();
  const { pathname } = useLocation();
  const sharedKitsEnabled = useFeature(FEATURES.sharedMarketingKits);
  const { classes } = useStyles({ isKiteUser, isMobileMode });

  const tabs = useMemo(
    () =>
      kitsTabs.filter(
        (tab) => sharedKitsEnabled || tab.path !== SHARED_KITS_PATH,
      ),
    [sharedKitsEnabled],
  );

  const activeTab = useMemo(() => {
    const index = tabs.findIndex((tab) => tab.path === pathname);

    return index === -1 ? 0 : index;
  }, [pathname, tabs]);

  return (
    <div className={classes.bar}>
      {isTabletMode && (
        <Typography variant={isMobileMode ? 'h1' : 'h2'}>My Kits</Typography>
      )}
      <Box className={classes.panel}>
        <Tabs
          value={activeTab}
          className={classes.tabs}
          TabIndicatorProps={{
            style: {
              backgroundColor: isKiteUser
                ? theme.palette.primary2
                : theme.palette.primary1,
            },
          }}
          aria-label="kits tabs">
          {tabs.map((tab) => (
            <Tab
              key={tab.path}
              className={classes.tab}
              label={tab.label}
              component={NavLink}
              to={tab.path}
            />
          ))}
        </Tabs>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={isMobileMode ? 'start' : 'end'}>
          {!isMobileMode && (
            <Search onChange={onChange} onClearSearch={onClearSearch}></Search>
          )}
          {withDeleteAll && (
            <IconButton
              data-testid={'delete-kits-button'}
              onClick={onDeleteAll}
              startIcon={<Feather type={'delete'} />}
              className={classes.deleteAllButton}>
              Delete All
            </IconButton>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default KitsBar;
